import type { DayPlan, RibbonSegment } from "@weather-deck/shared";
import { el } from "./dom";

/** Renders null/undefined measurements as an em dash rather than "null" or "undefined". */
function fmt(v: number | null): string {
  return v == null ? "–" : String(v);
}

function ariaLabel(seg: RibbonSegment): string {
  return `${seg.hour}h — ${seg.kind}, ${fmt(seg.temp)}°, ${fmt(seg.precipProb)} % rain, wind ${fmt(seg.wind)} km/h`;
}

function detailText(seg: RibbonSegment): string {
  return `${seg.hour}h — ${fmt(seg.temp)}°, rain ${fmt(seg.precipProb)} %, wind ${fmt(seg.wind)} km/h, UV ${fmt(seg.uv)}`;
}

/** Legend wording per ribbon kind — approved mockup shows a swatch legend under the band. */
const LEGEND_LABELS: Record<RibbonSegment["kind"], string> = {
  warning: "warning",
  rain: "rain",
  "uv-high": "UV high",
  hot: "hot",
  "good-outside": "good outside",
  neutral: "mixed",
  night: "night"
};

/** One swatch + label for EVERY kind on today's band — a legend that leaves
 *  the largest color masses unexplained is no legend (Craig, 16.08). */
function buildLegend(plan: DayPlan): HTMLElement | null {
  const present = new Set(plan.segments.map((s) => s.kind));
  const entries = (Object.keys(LEGEND_LABELS) as Array<keyof typeof LEGEND_LABELS>)
    .filter((kind) => present.has(kind));
  if (entries.length === 0) return null;
  return el("div", { className: "ribbon-legend" },
    entries.map((kind) => el("span", { className: "ribbon-legend-item" }, [
      el("span", { className: `ribbon-legend-swatch ribbon-${kind}` }),
      el("span", { text: LEGEND_LABELS[kind]! })
    ]))
  );
}

export function buildDayRibbon(plan: DayPlan): HTMLElement {
  const detail = el("div", { className: "ribbon-detail" });
  let activeHour: number | null = null;

  const buttons: HTMLButtonElement[] = plan.segments.map((seg) => {
    const btn = el("button", { className: `ribbon-seg ribbon-${seg.kind}` }) as HTMLButtonElement;
    btn.setAttribute("aria-label", ariaLabel(seg));
    btn.addEventListener("click", () => {
      if (activeHour === seg.hour) {
        activeHour = null;
        detail.textContent = "";
        btn.classList.remove("is-active");
        return;
      }
      for (const b of buttons) b.classList.remove("is-active");
      activeHour = seg.hour;
      detail.textContent = detailText(seg);
      btn.classList.add("is-active");
    });
    return btn;
  });

  const row = el("div", { className: "ribbon-row" }, buttons);
  const hours = el("div", { className: "ribbon-hours" },
    [0, 6, 12, 18, 24].map((h) => el("span", { text: String(h) }))
  );

  const children: Array<Node | null> = [
    el("div", { className: "ribbon-label", text: plan.label }),
    row,
    hours,
    buildLegend(plan),
    el("p", { className: "ribbon-caption", text: plan.caption })
  ];
  if (plan.hazard) children.push(el("p", { className: "ribbon-hazard", text: plan.hazard }));
  children.push(detail);

  return el("section", { className: "ribbon" }, children);
}
